import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Wifi, Clock, Check, ChevronRight, Download, Smartphone, Copy, ExternalLink, RefreshCw, QrCode, Plus, Settings, LogOut, Zap, Loader2, MessageCircle, AlertTriangle, X, Send, User, Globe } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import PWAInstallPrompt, { usePWADashboardTrigger } from '../components/PWAInstallPrompt';
import { AutoTopUpControl } from '../components/AutoTopUpControl';
import { isNative } from '../lib/platform';
import { installEsim } from '../lib/lpaInstall';
import { hapticSuccess, hapticMedium } from '../lib/haptics';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, mySims, logout, installSim, isAdmin, formatPrice, formatCredits, allCountries, getUserReferralInfo, refreshSims } = useApp();
  const [activeTab, setActiveTab] = useState('active');
  const pwaTrigger = usePWADashboardTrigger();
  const [selectedSim, setSelectedSim] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [copied, setCopied] = useState(false);
  // Set of SIM ids whose install info should be revealed (overrides the default-hide for installed/active eSIMs).
  const [revealedInstallInfo, setRevealedInstallInfo] = useState(() => new Set());
  const toggleInstallInfo = (simId) => {
    setRevealedInstallInfo((prev) => {
      const next = new Set(prev);
      if (next.has(simId)) next.delete(simId); else next.add(simId);
      return next;
    });
  };
  // Statuses where the eSIM is already on the user's device — install info hidden by default.
  const INSTALLED_LIKE_STATES = ['INSTALLED', 'ACTIVE', 'ACTIVATED', 'INACTIVE'];
  const isInstalledLike = (sim) => INSTALLED_LIKE_STATES.includes(sim.roamifyStatus || sim.status);
  const [checkingStatus, setCheckingStatus] = useState({});
  const [showAssistant, setShowAssistant] = useState(false);
  const [assistantMessages, setAssistantMessages] = useState([
    { role: 'assistant', content: "Hi! I'm your Connectify assistant. I can help you with:\n\n• Installing your eSIM\n• Troubleshooting connection issues\n• Understanding your data plan\n• Top-up questions\n\nHow can I help you today?" }
  ]);
  const [assistantInput, setAssistantInput] = useState('');
  const [assistantLoading, setAssistantLoading] = useState(false);
  const [assistantSessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  
  // Referral state
  const [showReferral, setShowReferral] = useState(false);
  const [referralInfo, setReferralInfo] = useState(null);
  const [referralCopied, setReferralCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  // Buy-again (reorder expired eSIM) state
  const [buyingAgain, setBuyingAgain] = useState({});

  const handleBuyAgain = async (sim) => {
    setBuyingAgain((p) => ({ ...p, [sim.id]: true }));
    try {
      const res = await axios.post(`${API}/esim/buy-again`, { iccid: sim.iccid, user_email: user.email });
      const d = res.data || {};
      if (d.success && d.mode === 'reordered') {
        await refreshSims();
        setActiveTab('active');
      } else if (d.mode === 'checkout_required') {
        navigate(`/destinations/${d.country_id}?buy=${encodeURIComponent(d.package_id)}`);
      }
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not complete the purchase. Please try again.');
    } finally {
      setBuyingAgain((p) => ({ ...p, [sim.id]: false }));
    }
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Auto-refresh eSIM data every 60 seconds
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      refreshSims();
    }, 60000);
    return () => clearInterval(interval);
  }, [user, refreshSims]);

  // Load referral info
  useEffect(() => {
    const loadReferralInfo = async () => {
      if (user?.email) {
        try {
          const info = await getUserReferralInfo(user.email);
          if (info) {
            setReferralInfo(info);
          }
        } catch (e) {
          console.error('Error loading referral info:', e);
        }
      }
    };
    loadReferralInfo();
  }, [user, getUserReferralInfo]);

  if (!user) return null;

  // States that belong in the expired/unusable tab
  const EXPIRED_STATES = ['EXPIRED', 'REVOKED', 'DELETED'];

  // Map API status to user-friendly display
  const getStatusDisplay = (sim) => {
    const status = sim.roamifyStatus || sim.status;
    switch (status) {
      case 'NEW': return { label: 'New', color: 'bg-blue-100 text-blue-800' };
      case 'READY': return { label: 'Ready to Install', color: 'bg-yellow-100 text-yellow-800' };
      case 'INSTALLED': return { label: 'Installed', color: 'bg-green-100 text-green-800' };
      case 'ACTIVE': return { label: 'Active', color: 'bg-green-100 text-green-800' };
      case 'REVOKED': return { label: 'Revoked', color: 'bg-red-100 text-red-800' };
      case 'EXPIRED': return { label: 'Expired', color: 'bg-gray-100 text-gray-600' };
      case 'DELETED': return { label: 'Expired', color: 'bg-gray-100 text-gray-600' };
      case 'PENDING': return { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' };
      case 'UNKNOWN': return { label: 'Unknown', color: 'bg-gray-100 text-gray-600' };
      default: return { label: status || 'Unknown', color: 'bg-gray-100 text-gray-600' };
    }
  };

  // Only move to expired tab if status is explicitly EXPIRED or REVOKED
  const activeSims = mySims.filter(s => !EXPIRED_STATES.includes(s.roamifyStatus || s.status));
  const expiredSims = mySims.filter(s => EXPIRED_STATES.includes(s.roamifyStatus || s.status));

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Check eSIM status via Roamify API
  const handleCheckStatus = async (sim) => {
    if (!sim.iccid) return;
    
    setCheckingStatus(prev => ({ ...prev, [sim.id]: true }));
    
    try {
      const response = await axios.get(`${API}/esim/usage/${sim.iccid}`);
      if (response.data.success) {
        const usageData = response.data.data;
        // Update the selected sim display with latest data
        setSelectedSim(prev => prev ? {
          ...prev,
          status: usageData.status,
          dataTotal: usageData.allowed_data_gb,
          dataUsed: usageData.used_data_gb,
          roamifyStatus: usageData.roamify_status
        } : prev);
        
        // Refresh the page to get updated data from context
        window.location.reload();
      }
    } catch (error) {
      console.error('Error checking status:', error);
    } finally {
      setCheckingStatus(prev => ({ ...prev, [sim.id]: false }));
    }
  };

  // Handle sending message to AI assistant
  const handleSendAssistantMessage = async () => {
    if (!assistantInput.trim() || assistantLoading) return;
    
    const userMessage = assistantInput.trim();
    setAssistantInput('');
    setAssistantMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setAssistantLoading(true);
    
    try {
      const response = await axios.post(`${API}/assistant/chat`, {
        message: userMessage,
        session_id: assistantSessionId,
        user_email: user?.email,
        context: {
          active_sims: activeSims.length,
          has_esim: mySims.length > 0
        }
      });
      
      if (response.data.success) {
        setAssistantMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
      } else {
        setAssistantMessages(prev => [...prev, { role: 'assistant', content: "I'm sorry, I couldn't process your request. Please try again or contact support@connectify.com" }]);
      }
    } catch (error) {
      // Fallback response for common queries
      let fallbackResponse = "I apologize, but I'm having trouble connecting right now. Here are some quick tips:\n\n";
      const lowerMessage = userMessage.toLowerCase();
      
      if (lowerMessage.includes('install') || lowerMessage.includes('setup')) {
        fallbackResponse += "**Installing your eSIM:**\n1. Use the 'Install on iPhone' or 'Install on Android' buttons above\n2. Or scan the QR code with your camera\n3. Follow the on-screen prompts\n4. Enable data roaming when you arrive at your destination";
      } else if (lowerMessage.includes('roaming') || lowerMessage.includes('connect')) {
        fallbackResponse += "**Connecting to data:**\n1. Go to Settings > Cellular/Mobile Data\n2. Select your Connectify eSIM\n3. Turn on Data Roaming\n4. Make sure the eSIM is set as your data line";
      } else if (lowerMessage.includes('top up') || lowerMessage.includes('topup') || lowerMessage.includes('more data')) {
        fallbackResponse += "**Adding more data:**\nClick the green 'Top Up' button on your eSIM card to purchase additional data for your plan.";
      } else {
        fallbackResponse += "For further assistance, please email support@connectify.com or try:\n• Check your eSIM status with the 'Check Status' button\n• Ensure data roaming is enabled\n• Restart your device";
      }
      
      setAssistantMessages(prev => [...prev, { role: 'assistant', content: fallbackResponse }]);
    } finally {
      setAssistantLoading(false);
    }
  };

  const displayedSims = activeTab === 'active' ? activeSims : expiredSims;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
      <Navbar />

      <div className="pt-28 pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div>
              <h1 className="text-3xl sm:text-4xl font-black text-gray-900 dark:text-neutral-100">My eSIMs</h1>
              <p className="text-gray-600 dark:text-neutral-400 mt-1">Manage your eSIM plans and usage</p>
            </div>
            <div className="flex items-center gap-3">
              {/* Account Settings - only show for email/password users */}
              {user && user.authProvider !== 'google' && user.authProvider !== 'facebook' && (
                <button
                  onClick={() => navigate('/account-settings')}
                  className="flex items-center gap-2 px-4 py-3 bg-white dark:bg-neutral-900 text-gray-700 dark:text-neutral-300 rounded-xl font-semibold hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors border border-gray-200 dark:border-neutral-800"
                  data-testid="account-settings-btn"
                >
                  <User size={18} /> Account
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => navigate('/admin')}
                  className="flex items-center gap-2 px-5 py-3 bg-gray-100 dark:bg-neutral-800 text-gray-900 dark:text-neutral-100 rounded-xl font-bold hover:bg-gray-200 dark:hover:bg-neutral-700 transition-colors"
                  data-testid="admin-dashboard-btn"
                >
                  <Settings size={18} /> Admin
                </button>
              )}
              <button
                onClick={() => navigate('/destinations')}
                className="flex items-center gap-2 px-5 py-3 bg-black text-white rounded-xl font-bold hover:bg-gray-800 transition-colors dark:border dark:border-white"
                data-testid="buy-new-esim-btn"
              >
                <Plus size={18} /> Buy New eSIM
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white dark:bg-neutral-900 rounded-2xl p-5 border border-gray-100 dark:border-neutral-800">
              <p className="text-sm text-gray-500 dark:text-neutral-500 mb-1">Active Plans</p>
              <p className="text-3xl font-black text-gray-900 dark:text-neutral-100">{activeSims.length}</p>
            </div>
            <div className="bg-white dark:bg-neutral-900 rounded-2xl p-5 border border-gray-100 dark:border-neutral-800">
              <p className="text-sm text-gray-500 dark:text-neutral-500 mb-1">Total Data</p>
              <p className="text-3xl font-black text-gray-900 dark:text-neutral-100">
                {activeSims.some(s => s.dataTotal > 100000) ? '∞' : 
                  activeSims.reduce((acc, s) => acc + s.dataTotal, 0).toFixed(1)}
                <span className="text-lg font-normal ml-1">GB</span>
              </p>
            </div>
            <div className="bg-white dark:bg-neutral-900 rounded-2xl p-5 border border-gray-100 dark:border-neutral-800">
              <p className="text-sm text-gray-500 dark:text-neutral-500 mb-1">Data Used</p>
              <p className="text-3xl font-black text-gray-900 dark:text-neutral-100">
                {activeSims.reduce((acc, s) => acc + (s.dataUsed || 0), 0).toFixed(1)}
                <span className="text-lg font-normal ml-1">GB</span>
              </p>
            </div>
            <div className="bg-white dark:bg-neutral-900 rounded-2xl p-5 border border-gray-100 dark:border-neutral-800">
              <p className="text-sm text-gray-500 dark:text-neutral-500 mb-1">Credit Balance</p>
              <p className="text-3xl font-black text-emerald-600 dark:text-emerald-400">
                {formatCredits(referralInfo?.creditBalance || user?.creditBalance || 0)}
              </p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setActiveTab('active')}
              className={`px-6 py-3 rounded-xl font-bold transition-colors dark:border dark:border-white ${
                activeTab === 'active'
                  ? 'bg-black text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:bg-neutral-800'
              }`}
            >
              Active ({activeSims.length})
            </button>
            <button
              onClick={() => setActiveTab('expired')}
              className={`px-6 py-3 rounded-xl font-bold transition-colors dark:border dark:border-white ${
                activeTab === 'expired'
                  ? 'bg-black text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:bg-neutral-800'
              }`}
            >
              Expired ({expiredSims.length})
            </button>
          </div>

          {/* SIMs List */}
          {displayedSims.length === 0 ? (
            <div className="bg-white dark:bg-neutral-900 rounded-2xl p-12 text-center border border-gray-100 dark:border-neutral-800">
              <Wifi className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-2">
                {activeTab === 'active' ? 'No active eSIMs' : 'No expired eSIMs'}
              </h3>
              <p className="text-gray-600 dark:text-neutral-400 mb-6">
                {activeTab === 'active' 
                  ? 'Purchase an eSIM to get started with global connectivity.'
                  : 'Your expired eSIMs will appear here.'}
              </p>
              {activeTab === 'active' && (
                <button
                  onClick={() => navigate('/destinations')}
                  className="px-8 py-4 bg-black text-white rounded-xl font-bold hover:bg-gray-800 transition-colors"
                >
                  Browse Destinations
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {displayedSims.map((sim) => (
                <div
                  key={sim.id}
                  className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-100 dark:border-neutral-800 overflow-hidden hover:border-gray-300 transition-colors"
                >
                  <div 
                    className="p-5 cursor-pointer"
                    onClick={() => setSelectedSim(selectedSim?.id === sim.id ? null : sim)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {/* Use Globe icon for regional plans, flag for countries */}
                        {sim.country?.isRegion || sim.isRegion ? (
                          <div className="w-14 h-10 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-lg shadow flex items-center justify-center">
                            <Globe size={20} className="text-white" />
                          </div>
                        ) : (
                          <img
                            src={sim.country?.flag}
                            alt={sim.country?.name}
                            className="w-14 h-10 object-cover rounded-lg shadow"
                          />
                        )}
                        <div>
                          <h3 className="font-bold text-gray-900 dark:text-neutral-100">{sim.country?.name}</h3>
                          <p className="text-sm text-gray-500 dark:text-neutral-500">{sim.planName}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right hidden sm:block">
                          <p className="text-sm text-gray-500 dark:text-neutral-500">Data Remaining</p>
                          <p className="text-lg font-bold text-gray-900 dark:text-neutral-100">
                            {sim.dataTotal > 100000 ? '∞' : `${(sim.dataTotal - (sim.dataUsed || 0)).toFixed(2)} GB`}
                          </p>
                        </div>
                        <div className="hidden sm:flex items-center gap-2">
                          {(() => { const s = getStatusDisplay(sim); return (
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${s.color}`}>
                              {s.label}
                            </span>
                          ); })()}
                        </div>
                        <ChevronRight 
                          className={`text-gray-400 dark:text-neutral-500 transition-transform ${selectedSim?.id === sim.id ? 'rotate-90' : ''}`} 
                          size={20} 
                        />
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {selectedSim?.id === sim.id && (
                    <div className="border-t border-gray-100 dark:border-neutral-800 p-5 bg-gray-50 dark:bg-neutral-950">
                      {/* eSIM Details Grid */}
                      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        <div>
                          <p className="text-xs text-gray-500 dark:text-neutral-500 mb-1">ICCID</p>
                          <p className="font-mono text-sm text-gray-900 dark:text-neutral-100">{sim.iccid || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-neutral-500 mb-1">Install Date</p>
                          <p className="text-sm text-gray-900 dark:text-neutral-100">{sim.purchaseDate}</p>
                        </div>
                        <div data-testid={`esim-expiry-date-${sim.id}`}>
                          <p className="text-xs text-gray-500 dark:text-neutral-500 mb-1">Expiry Date</p>
                          <p className="text-sm text-gray-900 dark:text-neutral-100">{sim.expiryDate || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-neutral-500 mb-1">Status</p>
                          <p className="text-sm text-gray-900 dark:text-neutral-100">{getStatusDisplay(sim).label}</p>
                        </div>
                      </div>

                      {/* eSIM Installation Section */}
                      {sim.qrCodeUrl && (() => {
                        const hideByDefault = isInstalledLike(sim);
                        const showInstall = !hideByDefault || revealedInstallInfo.has(sim.id);
                        return (
                          <div className="mb-6">
                            {hideByDefault && (
                              <button
                                type="button"
                                onClick={() => toggleInstallInfo(sim.id)}
                                className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-gray-50 dark:bg-neutral-950 hover:bg-gray-100 dark:hover:bg-neutral-800 border border-gray-200 dark:border-neutral-800 rounded-xl font-semibold text-sm text-gray-900 dark:text-neutral-100 transition-colors"
                                data-testid={`install-info-toggle-${sim.id}`}
                                aria-expanded={showInstall}
                              >
                                <span className="flex items-center gap-2">
                                  <Smartphone size={16} />
                                  Install info
                                </span>
                                <span className="text-xs text-gray-500 dark:text-neutral-500">{showInstall ? 'Hide' : 'Show'}</span>
                              </button>
                            )}
                            {showInstall && (
                              <div className={`bg-gradient-to-br from-gray-50 to-gray-100 dark:from-neutral-900 dark:to-neutral-950 border border-gray-100 dark:border-neutral-800 rounded-xl p-6 ${hideByDefault ? 'mt-3' : ''}`}>
                                {!hideByDefault && (
                                  <h4 className="font-bold text-gray-900 dark:text-neutral-100 mb-4 flex items-center gap-2">
                                    <Smartphone size={18} /> Install Your eSIM
                                  </h4>
                                )}
                          
                          {/* Primary Install Buttons */}
                          <div className="mb-6">
                            <p className="text-sm text-gray-600 dark:text-neutral-400 mb-4">
                              <strong>Recommended:</strong> Use the buttons below to install directly on your device.
                            </p>
                            <div className="flex flex-wrap gap-3">
                              {sim.iosQuickInstallUrl && (
                                <a
                                  href={sim.iosQuickInstallUrl}
                                  onClick={(e) => {
                                    if (isNative()) {
                                      e.preventDefault();
                                      hapticMedium();
                                      installEsim({
                                        lpa_code: sim.lpaCode,
                                        smdp_address: sim.smdpAddress,
                                        matching_id: sim.matchingId,
                                        activation_code: sim.activationCode,
                                      }).then((r) => { if (r?.ok) hapticSuccess(); });
                                    }
                                  }}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex-1 min-w-[140px] flex items-center justify-center gap-2 px-6 py-4 bg-black text-white rounded-xl font-bold hover:bg-gray-800 transition-colors"
                                  data-testid="ios-install-link"
                                >
                                  <svg style={{width:'35px',height:'35px'}} viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                                  </svg>
                                  Install on iPhone
                                </a>
                              )}
                              {sim.androidQuickInstallUrl && (
                                <a
                                  href={sim.androidQuickInstallUrl}
                                  onClick={(e) => {
                                    if (isNative()) {
                                      e.preventDefault();
                                      hapticMedium();
                                      installEsim({
                                        lpa_code: sim.lpaCode,
                                        smdp_address: sim.smdpAddress,
                                        matching_id: sim.matchingId,
                                        activation_code: sim.activationCode,
                                      }).then((r) => { if (r?.ok) hapticSuccess(); });
                                    }
                                  }}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex-1 min-w-[140px] flex items-center justify-center gap-2 px-6 py-4 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition-colors"
                                  data-testid="android-install-link"
                                >
                                  <svg style={{width:'35px',height:'35px'}} viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.523 2.377a.5.5 0 0 0-.882.052l-1.27 2.693a9.463 9.463 0 0 0-6.742 0L7.359 2.429a.5.5 0 0 0-.882-.052L5.2 4.7A9.537 9.537 0 0 0 2 12.5c0 .173.004.345.013.517h19.974c.009-.172.013-.344.013-.517 0-3.027-1.214-5.772-3.2-7.8l-1.277-2.323zM7 10a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm10 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2zM2.013 14.017A9.5 9.5 0 0 0 11.5 22h1a9.5 9.5 0 0 0 9.487-7.983H2.013z"/>
                                  </svg>
                                  Install on Android
                                </a>
                              )}
                            </div>
                          </div>

                          {/* QR Code as Alternative */}
                          <div className="border-t border-gray-200 dark:border-neutral-800 pt-5">
                            <p className="text-xs text-gray-500 dark:text-neutral-500 mb-3 uppercase font-semibold">Alternative: Scan QR Code</p>
                            <div className="flex flex-col sm:flex-row items-center gap-4">
                              <div className="flex flex-col items-center gap-2">
                                <div className="bg-white dark:bg-neutral-900 p-3 rounded-lg shadow-sm">
                                  <img 
                                    src={sim.qrCodeUrl} 
                                    alt="eSIM QR Code" 
                                    className="w-28 h-28 object-contain"
                                    data-testid="esim-qr-image"
                                  />
                                </div>
                                {sim.lpaCode && (
                                  <button
                                    onClick={() => handleCopy(sim.lpaCode)}
                                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-neutral-800 text-gray-900 dark:text-neutral-100 rounded-lg font-semibold text-sm hover:bg-gray-200 dark:hover:bg-neutral-700 transition-colors"
                                    data-testid="copy-lpa-btn"
                                  >
                                    {copied ? <Check size={16} /> : <Copy size={16} />}
                                    {copied ? 'Copied!' : 'Copy LPA'}
                                  </button>
                                )}
                              </div>
                              <p className="text-sm text-gray-500 dark:text-neutral-500 text-center sm:text-left">
                                To install your eSIM manually, either download the QR code and upload it into your phone&apos;s settings (in the &apos;add eSIM&apos; section) to install it, or scan the QR code with your phone&apos;s camera.
                              </p>
                            </div>
                          </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      
                      {/* Roaming Reminder */}
                      <div className="mb-6 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                        <AlertTriangle className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" size={20} />
                        <div>
                          <p className="font-semibold text-amber-800 dark:text-amber-300 text-sm">Important Reminder</p>
                          <p className="text-amber-700 dark:text-amber-300 text-sm mt-1">
                            Remember to turn on <strong>Data Roaming</strong> for your Connectify eSIM when you arrive at your destination.
                          </p>
                        </div>
                      </div>

                      {/* Auto Top-Up Control (active SIMs only) */}
                      {activeTab === 'active' && (
                        <div className="mb-6">
                          <AutoTopUpControl sim={sim} />
                        </div>
                      )}

                      {/* Data Usage Bar */}
                      {sim.dataTotal <= 100000 ? (
                        <div className="mb-6">
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-gray-600 dark:text-neutral-400">Data Usage</span>
                            <span className="font-semibold text-gray-900 dark:text-neutral-100">
                              {(sim.dataUsed || 0).toFixed(2)} / {sim.dataTotal} GB
                            </span>
                          </div>
                          <div className="h-2 bg-gray-200 dark:bg-neutral-700 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-black rounded-full transition-all"
                              style={{ width: `${Math.min(((sim.dataUsed || 0) / sim.dataTotal) * 100, 100)}%` }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="mb-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 flex items-center gap-3">
                          <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-500/15 rounded-full flex items-center justify-center">
                            <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">∞</span>
                          </div>
                          <div>
                            <p className="font-bold text-emerald-800">Unlimited Data</p>
                            <p className="text-sm text-emerald-600 dark:text-emerald-400">Enjoy unlimited data on this plan</p>
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex flex-wrap gap-3">
                        {sim.iccid && (
                          <button
                            onClick={() => handleCheckStatus(sim)}
                            disabled={checkingStatus[sim.id]}
                            className="flex items-center gap-2 px-5 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors disabled:opacity-50"
                            data-testid="check-status-btn"
                          >
                            {checkingStatus[sim.id] ? (
                              <>
                                <Loader2 size={18} className="animate-spin" /> Checking...
                              </>
                            ) : (
                              <>
                                <RefreshCw size={18} /> Check Status
                              </>
                            )}
                          </button>
                        )}
                        {!EXPIRED_STATES.includes(sim.roamifyStatus || sim.status) && sim.iccid && (
                          <button
                            onClick={() => navigate(`/topup/${sim.iccid}`)}
                            className="flex items-center gap-2 px-5 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-colors"
                            data-testid="topup-btn"
                          >
                            <Plus size={18} /> Top Up
                          </button>
                        )}
                        {EXPIRED_STATES.includes(sim.roamifyStatus || sim.status) && (
                          <div className="flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 rounded-xl text-sm font-medium">
                              <AlertTriangle size={16} />
                              {(sim.roamifyStatus || sim.status) === 'REVOKED' ? 'Revoked' : 'Expired'}
                            </div>
                            <button
                              onClick={() => handleBuyAgain(sim)}
                              disabled={buyingAgain[sim.id]}
                              className="flex items-center gap-2 px-5 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-colors disabled:opacity-50"
                              data-testid="buy-again-btn"
                            >
                              {buyingAgain[sim.id] ? (
                                <><Loader2 size={18} className="animate-spin" /> Processing...</>
                              ) : (
                                <><RefreshCw size={18} /> Buy again</>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Refresh eSIMs Button */}
          <div className="mt-8 text-center">
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 px-5 py-3 bg-gray-100 dark:bg-neutral-800 text-gray-700 dark:text-neutral-300 rounded-full font-medium hover:bg-gray-200 dark:hover:bg-neutral-700 transition-all text-sm"
              data-testid="refresh-esims-btn"
            >
              <RefreshCw size={16} />
              Just bought an eSIM that hasn&apos;t appeared yet? Wait 10 seconds then press me
            </button>
          </div>
          
          {/* Referral Program Section - Moved here between refresh and assistance */}
          <div className="mt-6 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-6 text-white">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold mb-1">Refer Friends & Earn Credit</h3>
                <p className="text-emerald-100 text-sm">
                  Share your code and earn A${referralInfo?.settings?.credit_value_aud || 5} when friends purchase
                </p>
              </div>
              <button
                onClick={() => setShowReferral(!showReferral)}
                className="px-5 py-2.5 bg-white dark:bg-neutral-900 text-emerald-700 dark:text-emerald-300 rounded-xl font-bold hover:bg-emerald-50 transition-colors whitespace-nowrap text-sm"
                data-testid="show-referral-btn"
              >
                {showReferral ? 'Hide Details' : 'Share & Earn'}
              </button>
            </div>

            {showReferral && (
              <div className="mt-5 pt-5 border-t border-white/20">
                <div className="grid sm:grid-cols-2 gap-4">
                  {/* Referral Code */}
                  <div className="bg-white/10 rounded-xl p-4">
                    <p className="text-emerald-100 text-sm mb-2">Your Referral Code</p>
                    <div className="flex items-center gap-3">
                      <span className="text-xl font-mono font-bold tracking-wider">
                        {referralInfo?.referralCode || user?.referralCode || 'Loading...'}
                      </span>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(referralInfo?.referralCode || user?.referralCode || '');
                          setReferralCopied(true);
                          setTimeout(() => setReferralCopied(false), 2000);
                        }}
                        className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
                        data-testid="copy-referral-code-btn"
                      >
                        {referralCopied ? <Check size={18} /> : <Copy size={18} />}
                      </button>
                    </div>
                  </div>

                  {/* Referral Link */}
                  <div className="bg-white/10 rounded-xl p-4">
                    <p className="text-emerald-100 text-sm mb-2">Your Referral Link</p>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono truncate flex-1">
                        {`${window.location.origin}?ref=${referralInfo?.referralCode || user?.referralCode || ''}`}
                      </span>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}?ref=${referralInfo?.referralCode || user?.referralCode || ''}`);
                          setLinkCopied(true);
                          setTimeout(() => setLinkCopied(false), 2000);
                        }}
                        className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
                        data-testid="copy-referral-link-btn"
                      >
                        {linkCopied ? <Check size={18} /> : <Copy size={18} />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Referral Stats */}
                <div className="grid grid-cols-3 gap-3 mt-4">
                  <div className="bg-white/10 rounded-xl p-3 text-center">
                    <p className="text-2xl font-black">{referralInfo?.totalReferrals || 0}</p>
                    <p className="text-emerald-100 text-xs">Total Referrals</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-3 text-center">
                    <p className="text-2xl font-black">{referralInfo?.successfulReferrals || 0}</p>
                    <p className="text-emerald-100 text-xs">Successful</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-3 text-center">
                    <p className="text-2xl font-black">{formatCredits(referralInfo?.creditBalance || user?.creditBalance || 0)}</p>
                    <p className="text-emerald-100 text-xs">Credits Earned</p>
                  </div>
                </div>

                {/* How it works */}
                <div className="mt-4 p-3 bg-white/10 rounded-xl">
                  <p className="font-bold mb-2 text-sm">How it works:</p>
                  <ol className="text-xs text-emerald-100 space-y-1 list-decimal list-inside">
                    <li>Share your code or link with friends</li>
                    <li>They get {(referralInfo?.settings?.referred_discount || 0.10) * 100}% off their first purchase</li>
                    <li>You earn A${referralInfo?.settings?.credit_value_aud || 5} credit when they activate their eSIM</li>
                    <li>Use credits on your future purchases</li>
                  </ol>
                </div>
              </div>
            )}
          </div>
          
          {/* I Need Assistance Button */}
          <div className="mt-4 text-center">
            <button
              onClick={() => setShowAssistant(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-full font-bold hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl"
              data-testid="assistance-btn"
            >
              <MessageCircle size={20} />
              I need assistance
            </button>
            <p className="text-gray-500 dark:text-neutral-500 text-sm mt-2">Get help with installation, connectivity, or any questions</p>
          </div>
        </div>
      </div>

      {/* AI Assistant Modal */}
      {showAssistant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAssistant(false)}>
          <div className="bg-white dark:bg-neutral-900 rounded-3xl max-w-lg w-full max-h-[80vh] flex flex-col overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <MessageCircle className="text-white" size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-white">Connectify Assistant</h3>
                  <p className="text-white/80 text-xs">Here to help 24/7</p>
                </div>
              </div>
              <button 
                onClick={() => setShowAssistant(false)}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-neutral-950">
              {assistantMessages.map((msg, idx) => (
                <div key={`${msg.role}-${idx}-${msg.content.substring(0, 20)}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] p-4 rounded-2xl ${
                    msg.role === 'user' 
                      ? 'bg-purple-600 text-white rounded-br-md' 
                      : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              {assistantLoading && (
                <div className="flex justify-start">
                  <div className="bg-white dark:bg-neutral-900 p-4 rounded-2xl rounded-bl-md shadow-sm border border-gray-100 dark:border-neutral-800">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-purple-600 dark:text-purple-400" />
                      <span className="text-sm text-gray-500 dark:text-neutral-500">Typing...</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Input */}
            <div className="p-4 border-t border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={assistantInput}
                  onChange={(e) => setAssistantInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendAssistantMessage()}
                  placeholder="Type your question..."
                  className="flex-1 px-4 py-3 border border-gray-200 dark:border-neutral-800 rounded-xl focus:outline-none focus:border-purple-500 text-sm"
                />
                <button
                  onClick={handleSendAssistantMessage}
                  disabled={!assistantInput.trim() || assistantLoading}
                  className="px-4 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  <Send size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQR && selectedSim && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowQR(false)}>
          <div className="bg-white dark:bg-neutral-900 rounded-3xl p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-2 text-center">Scan to Install</h3>
            <p className="text-gray-500 dark:text-neutral-500 text-center text-sm mb-6">
              Use the one-tap button, or manually scan a qr code, to install your eSIM
            </p>
            <div className="bg-gray-50 dark:bg-neutral-950 rounded-2xl p-6 flex justify-center mb-6">
              <QRCodeSVG 
                value={selectedSim.lpaCode || `LPA:1$${selectedSim.iccid || 'example.com'}$${selectedSim.imsi || 'activation'}`}
                size={200}
                level="H"
              />
            </div>
            <div className="space-y-3">
              <div className="text-center">
                <p className="text-xs text-gray-500 dark:text-neutral-500 mb-1">Or enter manually:</p>
                <p className="font-mono text-sm bg-gray-100 dark:bg-neutral-800 px-4 py-2 rounded-xl break-all">
                  {selectedSim.lpaCode || `LPA:1$${selectedSim.iccid}$activation`}
                </p>
              </div>
              <button
                onClick={() => setShowQR(false)}
                className="w-full py-4 bg-black text-white rounded-xl font-bold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <PWAInstallPrompt trigger={pwaTrigger} />
      <Footer />
    </div>
  );
};

export default Dashboard;
